import {
  createAuthorizationHeaders,
  observableFetchClient,
} from '@vp/common-api-utils';
import { getAuthHeaderAndShopperId } from '@vp/common-api-utils/authHelpers';
import {
  trackFetchOrderError,
  trackGetOrderFulfillmentHistoryError,
} from './newrelic/bopNewRelicTrackingApi';
import type { OrderSummariesResponse } from '../types/OrderHistory.types';
import type { BOPOrderSummariesResponse } from '../types/bopApi.types';
import type { ReorderableItemsResponse } from '../types/ReorderableItems.types';
import { mapOrderSummariesResponse } from '../utils/mapBopResponse';
import type { OrderFulfillmentHistory } from '@vp/fulfillment-history';

const prodBopBaseUrl = 'https://bop.orders.vpsvc.com';
const stagingBopBaseUrl = 'https://bop-staging.orders.vpsvc.com';

/**
 * @param {string} environment The environment to send BOP service requests too.
 * @return {string} The base URL for the BOP service.
 */
const getBOPBaseUrl = (environment: string): string => {
  if (!environment) {
    console.error(
      `Unable to derive service config for a null/empty environment, using STAGING environment as sensible fallback.`
    );
    return stagingBopBaseUrl;
  }
  switch (environment) {
    case 'PRODUCTION':
      return prodBopBaseUrl;
    case 'STAGING':
      return stagingBopBaseUrl;
    default:
      console.warn(
        `Unsupported environment type of ${environment}, using STAGING environment as sensible fallback.`
      );
      return stagingBopBaseUrl;
  }
};

type GetOrderSummaryParams = {
  environment: string;
  offset: number;
  limit: number;
  vistacartTenantName: string;
  shopperId: string;
};

type GetReorderableItemsParams = {
  shopperId: string;
  environment: string;
  vistacartTenantName: string;
  page: number;
  pageSize: number;
  includeRetired?: boolean;
};
/**
 * Fetches a summary of orders for a shopper.
 * @param {GetOrderSummaryParams} params - The parameters for fetching order summaries.
 * @returns {Promise<any>} An array of order summaries for the specified shopper.
 */
export const getOrdersSummary = async ({
  environment,
  offset,
  limit,
  vistacartTenantName,
  shopperId,
}: GetOrderSummaryParams): Promise<OrderSummariesResponse> => {
  const baseUrl = getBOPBaseUrl(environment);

  const url = `${baseUrl}/ui/shopper/${shopperId}/orders/summaries`;

  const { authHeader } = getAuthHeaderAndShopperId();
  try {
    const response = await observableFetchClient({
      url: url,
      method: 'get',
      headers: createAuthorizationHeaders(authHeader),
      params: {
        offset,
        limit,
        tenantId: vistacartTenantName,
      },
    });
    const data = (await response.json()) as BOPOrderSummariesResponse;
    return mapOrderSummariesResponse(data);
  } catch (err) {
    if (err instanceof Error) {
      trackFetchOrderError(err);
    }
    throw err;
  }
};

type GetOrderFulfillmentHistoryParams = {
  orderNumber: string;
  shopperId: string;
  environment: string;
};

/**
 * Fetches the fulfillment history for a specific order.
 * @param {GetOrderFulfillmentHistoryParams} params - The parameters for fetching the fulfillment history.
 * @returns {Promise<OrderFulfillmentHistory | undefined>} The fulfillment history data for the specified order.
 */
export const getOrderFulfillmentHistory = async ({
  orderNumber,
  shopperId,
  environment,
}: GetOrderFulfillmentHistoryParams): Promise<
  OrderFulfillmentHistory | undefined
> => {
  const metricsData = {
    validationOrderNumber: orderNumber,
    shopperId,
    environment,
  };

  const baseUrl = getBOPBaseUrl(environment);
  const url = `${baseUrl}/ui/shoppers/${shopperId}/orders/${orderNumber}/fulfillmentHistory`;
  const { authHeader } = getAuthHeaderAndShopperId();
  try {
    const response = await observableFetchClient({
      url: url,
      method: 'get',
      headers: createAuthorizationHeaders(authHeader),
    });

    return (await response.json()) as OrderFulfillmentHistory;
  } catch (err) {
    if (err instanceof Error) {
      trackGetOrderFulfillmentHistoryError(err, metricsData);
    }
    // in the case of an error (unlikely) do not throw, as this will blow up the entire page, and customers may not even want to see fulfillment history
    return undefined;
  }
};

type GetOrderDetailsParams = {
  orderNumber: string;
  environment: string;
  shopperId: string;
};

/**
 * @param {GetOrderDetailsParams} GetOrderDetailsParams
 * @returns {Promise<object>} The details of the order with the given orderId.
 */
export const getOrderDetails = async ({
  orderNumber,
  environment,
  shopperId,
}: GetOrderDetailsParams): Promise<{ orderNumber: string }> => {
  const baseUrl = getBOPBaseUrl(environment);
  const url = `${baseUrl}/ui/shopper/${shopperId}/orders/${orderNumber}`;
  const { authHeader } = getAuthHeaderAndShopperId();
  try {
    const response = await observableFetchClient({
      url: url,
      method: 'get',
      headers: createAuthorizationHeaders(authHeader),
    });
    const orderDetails = await response.json();
    return orderDetails;
  } catch (err) {
    if (err instanceof Error) {
      trackFetchOrderError(err, orderNumber);
    }
    throw err;
  }
};

export const getReorderableItems = async ({
  shopperId,
  environment,
  vistacartTenantName,
  page,
  pageSize,
  includeRetired = false,
}: GetReorderableItemsParams): Promise<ReorderableItemsResponse> => {
  const baseUrl = getBOPBaseUrl(environment);
  const params = { page, pageSize, includeRetired };
  const { authHeader } = getAuthHeaderAndShopperId();

  const url = `${baseUrl}/reorderableItems/${vistacartTenantName}/shopper/${shopperId}`;
  const response = await observableFetchClient({
    url,
    // retry 500 responses once
    maxAttempts: 2,
    params,
    method: 'get',
    headers: createAuthorizationHeaders(authHeader),
  });
  return await response.json();
};
